//src/design-system/Organisms/TestimonialSection/index.tsx
import { Typography } from "@/design-system/Atoms";
import { AppEmptyState } from "@/design-system/Feedbacks";
import { DASHBOARD_TABS } from "@/constants/dashboardTabs";
import TestimonialTabShimmer from "./TestimonialTabShimmer";
import { homeImageWithTextConfig } from "@/design-system/Organisms/ImageWithTextSection/imageWithText.config";
import { ImageSlider } from "@/design-system/Molecules";
import { TextAreaField } from "@/design-system/Atoms/TextArea";
import { Button } from "@/design-system/Atoms";
import { useTestimonialsService } from "@/rtkQuery/features/hooks/useTestimonialsService";
import { usePatientInformationService } from "@/rtkQuery/features/hooks/usePatientInformationService";
import { toastService } from "@/utils/toastService";
import { useForm } from "react-hook-form";
import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  testimonialFormSchema,
  type TestimonialFormSchema,
} from "@/validation-schemas/testimony-schemas";
import { useScrollToTopOnMount } from "@/hooks/useScrollToTopOnMount";

export const TestimonialSection = () => {
   useScrollToTopOnMount();
  const {
    testimonialData,
    testimonialIsLoading,
    testimonialIsError,
    submitTestimonial,
    submitting,
  } = useTestimonialsService();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<TestimonialFormSchema>({
    resolver: zodResolver(testimonialFormSchema),
    defaultValues: {
      firstName: "",
      message: "",
    },
    mode: "onTouched",
  });

  const { getPatientInformation } = usePatientInformationService();
  useEffect(() => {
    if (getPatientInformation?.FullName) {
      const firstName = getPatientInformation?.FullName?.split(" ")[0] ?? "";
      setValue("firstName", firstName);
    }
  }, [getPatientInformation, setValue]);

  const onSubmit = async (data: TestimonialFormSchema) => {
    try {
      await submitTestimonial(data).unwrap();
      reset({
        firstName:
          (getPatientInformation as any)?.FullName?.split(" ")[0] ?? "",
        message: "",
      });
      toastService.success("Testimonial submitted successfully!");
    } catch {
      toastService.error("Failed to submit testimonial.");
    }
  };

  return (
    <div className="rounded-[20px] bg-[#f6f7f9] p-8" id="v-pills-tabContent">
      <div
        id={DASHBOARD_TABS.TESTIMONIAL}
        role="tabpanel"
        aria-labelledby={DASHBOARD_TABS.TESTIMONIAL}
        className="block opacity-100"
      >
        <Typography variant="darkBackgroundHeading" as="h3" className="animate-fade-in-up">
          Testimonials
        </Typography>
        <div className="container mx-auto">
          <div className="max-w-[54rem] mx-auto bg-[#f6f7f9] rounded-2xl">
            <form
              id="dashboard-testimonial-section"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
            >
              <input
                type="text"
                className="hidden"
                placeholder="First name"
                {...register("firstName")}
                readOnly
              />
              <div className="mb-6">
                <TextAreaField
                  placeholder="Type your testimony..."
                  rows={3}
                  {...register("message")}
                  error={errors.message?.message}
                />
              </div>
              <Button
                variant="secondary"
                size="xl"
                className="!w-full mb-10"
                type="submit"
                disabled={submitting}
                loading={submitting}
              >
                {submitting ? "Submitting..." : "Submit"}
              </Button>
            </form>
          </div>
          {testimonialIsLoading ? (
            <TestimonialTabShimmer />
          ) : !testimonialIsLoading && testimonialIsError && (testimonialData?.length ?? 0) === 0 ? (
            <div className="w-fit mx-auto px-4">
              <div className="rounded-[20px] min-h-[40vh] lg:min-h-[35vh] flex items-center justify-center">
                <AppEmptyState message="No testimonials found." />
              </div>
            </div>
          ) : (
            <div className="lg:w-3/4 w-full mx-auto rounded-2xl backdrop-blur-md bg-white p-6 ">
              {homeImageWithTextConfig.variant === "highlighted" &&
                homeImageWithTextConfig.highlightedHeadingMain &&
                homeImageWithTextConfig.highlightedHeadingAccent ? (
                <Typography variant="sectionHeading" as="h2" className="mb-6">
                  <>
                    {homeImageWithTextConfig.highlightedHeadingMain}{" "}
                    <span className="text-gray-400">
                      {homeImageWithTextConfig.highlightedHeadingAccent}
                    </span>
                  </>
                </Typography>
              ) : null}

              <div className="relative min-h-screen sm:min-h-[370px] md:min-h-[240px] lg:min-h-[300px] w-full">
                {homeImageWithTextConfig.showSwiper &&
                  (!testimonialIsLoading && testimonialData && testimonialData?.length > 0 && (
                    <div className="absolute inset-0 w-full h-full">
                      <ImageSlider
                        testimonials={testimonialData ?? []}
                        pageName="TabPage"
                      />
                    </div>
                  )
                  )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default TestimonialSection;
